import { Stack } from "@mui/material";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { MemberRole } from "../../../../../api/types/userManagementTypes";
import type { CompanyAccessLevel, CompanyWithAccess } from "../definitions";
import DistributeCompanyPermissions from "./DistributeCompanyPermissions";
import EntitiesAccess from "./EntitiesAccess";
import InviteMembersContext from "./InviteMembersContext";
import ProductPermissions from "./ProductPermissions";

interface Props {
  companyAccess: [CompanyWithAccess, CompanyAccessLevel];
}

const AccessSection = ({ companyAccess }: Props) => {
  const { selectedCompanies, updateSelectedCompanies, updateSelectedCompanyAccess } =
    useDefinedContext(InviteMembersContext);

  const [company, accessLevel] = companyAccess;

  const handleSelectedRolesChange = (selectedRoles: MemberRole[]) => {
    updateCompany({ roles: selectedRoles });
  };

  const handleSelectedEntityIdsChange = (selectedEntityIds: string[], accessToAllEntities: boolean) => {
    updateCompany({ entityIds: selectedEntityIds, accessToAllEntities });
  };

  const updateCompany = (update: Partial<CompanyWithAccess>) => {
    const companyUpdate = {
      ...company,
      ...update,
    };
    updateSelectedCompanyAccess(companyUpdate, accessLevel);
    updateSelectedCompanies([...selectedCompanies.filter((c) => c.clientCode !== company.clientCode), companyUpdate]);
  };

  return (
    <Stack sx={{ justifyContent: "space-between", height: "100%", width: "100%" }}>
      {accessLevel === "Product" && (
        <>
          <ProductPermissions
            clientCode={company.clientCode}
            selectedRoles={company.roles}
            onSelectedRolesChange={handleSelectedRolesChange}
          />
          {selectedCompanies.length > 1 && <DistributeCompanyPermissions />}
        </>
      )}
      {accessLevel === "Entity" && (
        <EntitiesAccess
          clientCode={company.clientCode}
          selectedEntityIds={company.entityIds}
          onSelectedEntityIdsChange={handleSelectedEntityIdsChange}
          accessToAllEntities={company.accessToAllEntities}
        />
      )}
    </Stack>
  );
};

export default AccessSection;
